/* Skincare — weekly skincare rituals with daily reminder card. */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Check, Sparkles, Clock, Edit2, X, Save, Flame, Sun, Moon, ChevronDown, ChevronRight, Bell, Smile, Timer, TrendingUp,
} from 'lucide-react';
import SheetMaskTimer from './SheetMaskTimer';
import ProductInventory from './ProductInventory';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function WhenBadge({ when }) {
  const map = {
    'morgon': { icon: Sun, cls: 'bg-amber-50 border-amber-200 text-amber-800' },
    'kväll': { icon: Moon, cls: 'bg-violet-50 border-violet-200 text-violet-800' },
    'morgon+kväll': { icon: Sun, cls: 'bg-sky-50 border-sky-200 text-sky-800', label: 'morgon + kväll' },
    'duschtid': { icon: Sun, cls: 'bg-cyan-50 border-cyan-200 text-cyan-800' },
  };
  const m = map[when] || { icon: Clock, cls: 'bg-slate-50 border-slate-200 text-slate-700' };
  const Icon = m.icon;
  return (
    <span className={`text-[10px] rounded-full px-1.5 py-0.5 border inline-flex items-center gap-0.5 ${m.cls}`}>
      <Icon className="w-2.5 h-2.5" /> {m.label || when}
    </span>
  );
}

export default function Skincare() {
  const [plan, setPlan] = useState(null);
  const [today, setToday] = useState(null);
  const [streak, setStreak] = useState({ streak: 0, days: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [editingDay, setEditingDay] = useState(null);
  const [showBase, setShowBase] = useState(false);
  const [moodOptions, setMoodOptions] = useState([]);
  const [todayMood, setTodayMood] = useState(null);
  const [selectedMoods, setSelectedMoods] = useState([]);
  const [pushingToFlow, setPushingToFlow] = useState(false);
  const [showSheetMask, setShowSheetMask] = useState(false);
  const [insights, setInsights] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: t }, { data: s }, { data: mo }, { data: mh }, { data: ci }] = await Promise.all([
        axios.get(`${API}/skincare/plan`, { withCredentials: true }),
        axios.get(`${API}/skincare/today`, { withCredentials: true }),
        axios.get(`${API}/skincare/streak?days=14`, { withCredentials: true }),
        axios.get(`${API}/skincare/mood/options`, { withCredentials: true }).catch(() => ({ data: { moods: [] } })),
        axios.get(`${API}/skincare/mood/history?days=7`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API}/skincare/mood/correlation`, { withCredentials: true }).catch(() => ({ data: { insights: [] } })),
      ]);
      setPlan(p);
      setToday(t);
      setStreak(s);
      setMoodOptions(mo.moods || []);
      setTodayMood(mh.today || null);
      setSelectedMoods((mh.today && mh.today.moods) ? mh.today.moods : []);
      setInsights(ci.insights || []);
    } catch {
      toast.error('Kunde inte ladda hudrutinen');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (stepId, isDone) => {
    if (!today) return;
    try {
      if (isDone) {
        await axios.delete(
          `${API}/skincare/completed?day_key=${today.day_key}${stepId ? `&step_id=${stepId}` : ''}`,
          { withCredentials: true },
        );
      } else {
        await axios.post(
          `${API}/skincare/completed`,
          { day: today.day_key, step_id: stepId || null },
          { withCredentials: true },
        );
        if (!stepId) toast.success('Kvällens ritual avbockad 💜');
      }
      await load();
    } catch {
      toast.error('Kunde inte spara');
    }
  };

  const savePlan = async (dayKey, updates) => {
    try {
      const weekly = { ...plan.weekly, [dayKey]: { ...(plan.weekly[dayKey] || {}), ...updates } };
      const { data } = await axios.put(`${API}/skincare/plan`, { weekly }, { withCredentials: true });
      setPlan((p) => ({ ...p, weekly: data.weekly }));
      setEditingDay(null);
      toast.success('Sparat 💜');
    } catch { toast.error('Kunde inte spara'); }
  };

  const pushToFlow = async () => {
    setPushingToFlow(true);
    try {
      const { data } = await axios.post(`${API}/skincare/push-to-daily-flow`, {}, { withCredentials: true });
      toast.success(data.created ? 'Tillagd i Idag-lista 🔔' : 'Redan i Idag-lista');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kunde inte skicka');
    } finally { setPushingToFlow(false); }
  };

  const toggleMood = (m) => {
    setSelectedMoods((s) => (s.includes(m) ? s.filter((x) => x !== m) : (s.length < 6 ? [...s, m] : s)));
  };

  const saveMood = async () => {
    if (selectedMoods.length === 0) { toast('Välj minst ett humör'); return; }
    try {
      await axios.post(`${API}/skincare/mood`, { moods: selectedMoods }, { withCredentials: true });
      setTodayMood({ moods: selectedMoods, notes: '' });
      toast.success('Sparat 💜');
    } catch { toast.error('Kunde inte spara humör'); }
  };

  if (loading || !plan || !today) {
    return (
      <div className="text-center py-12 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Laddar…
      </div>
    );
  }

  const isRitualDone = today.completed.some((c) => !c.step_id);
  const doneStepIds = new Set(today.completed.filter((c) => c.step_id).map((c) => c.step_id));

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="skincare-page">
      {/* Hero + today's ritual */}
      <div className="rounded-3xl p-5 bg-gradient-to-br from-rose-100 via-pink-100 to-fuchsia-100 border border-rose-200 relative overflow-hidden">
        <div className="absolute -right-4 -top-4 opacity-15 text-8xl leading-none pointer-events-none">{today.day_emoji}</div>
        <div className="text-[11px] uppercase font-semibold tracking-wider text-rose-800/80">
          Hudrutin · {today.day_label}
        </div>
        <h1 className="text-2xl font-bold text-rose-900" style={{ fontFamily: 'ui-serif, Georgia, serif' }}>
          {today.ritual?.ritual || 'Ingen speciell rutin idag'}
        </h1>
        {today.ritual?.desc && (
          <p className="text-sm text-rose-900/80 mt-2 leading-relaxed">{today.ritual.desc}</p>
        )}
        <div className="flex flex-wrap items-center gap-1.5 mt-3">
          {today.ritual?.when && <WhenBadge when={today.ritual.when} />}
          {today.ritual?.time_min && (
            <span className="text-[10px] rounded-full px-1.5 py-0.5 border bg-white/60 border-rose-200 text-rose-800 inline-flex items-center gap-0.5">
              <Clock className="w-2.5 h-2.5" /> {today.ritual.time_min} min
            </span>
          )}
          {(today.ritual?.tags || []).map((t) => (
            <span key={t} className="text-[10px] rounded-full px-1.5 py-0.5 bg-white/60 border border-rose-200 text-rose-800">
              #{t}
            </span>
          ))}
          {streak.streak > 0 && (
            <span className="text-[10px] rounded-full px-1.5 py-0.5 border bg-amber-50 border-amber-200 text-amber-800 inline-flex items-center gap-0.5">
              <Flame className="w-2.5 h-2.5" /> {streak.streak} dagar streak
            </span>
          )}
        </div>
        {today.ritual?.ritual && (
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => toggle(null, isRitualDone)}
              data-testid="skincare-toggle-today"
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition ${
                isRitualDone
                  ? 'bg-emerald-100 border-2 border-emerald-500 text-emerald-800'
                  : 'bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white hover:opacity-90'
              }`}
            >
              {isRitualDone ? <><Check className="w-4 h-4" /> Klarat idag ✨</> : <><Sparkles className="w-4 h-4" /> Markera som klar</>}
            </button>
            <button
              type="button"
              onClick={pushToFlow}
              disabled={pushingToFlow}
              data-testid="skincare-push-to-flow"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs bg-white/70 border border-rose-300 text-rose-800 hover:bg-white disabled:opacity-50 font-medium"
            >
              {pushingToFlow ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bell className="w-3 h-3" />}
              Skicka till Idag-lista
            </button>
            {/(sheet|mask)/i.test(today.ritual?.ritual || '') && (
              <button
                type="button"
                onClick={() => setShowSheetMask(true)}
                data-testid="skincare-sheet-mask-btn"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white font-medium hover:opacity-90"
              >
                <Timer className="w-3 h-3" /> Starta 15-min mask-timer
              </button>
            )}
          </div>
        )}
      </div>

      {showSheetMask && <SheetMaskTimer onClose={() => setShowSheetMask(false)} />}

      {/* Skin mood picker */}
      {moodOptions.length > 0 && (
        <div className="rounded-2xl bg-white border border-rose-100 p-4" data-testid="skincare-mood-picker">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
              <Smile className="w-3.5 h-3.5 text-rose-500" /> Hur känns huden idag?
            </div>
            {todayMood && (
              <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                ✓ Loggat idag
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {moodOptions.map((m) => {
              const chosen = selectedMoods.includes(m);
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => toggleMood(m)}
                  data-testid={`skincare-mood-${m}`}
                  className={`text-xs px-2.5 py-1 rounded-full border transition ${
                    chosen
                      ? 'bg-rose-500 text-white border-rose-500'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-rose-300'
                  }`}
                >
                  {chosen ? '✓ ' : ''}{m}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={saveMood}
            disabled={selectedMoods.length === 0}
            data-testid="skincare-mood-save"
            className="text-xs px-3 py-1 rounded-full bg-rose-500 hover:bg-rose-600 text-white font-semibold disabled:opacity-50"
          >
            Spara humör
          </button>
          <p className="text-[10px] text-slate-400 italic mt-2">
            Loggas som mönster i söndagsrekapen så du ser trender över veckan.
          </p>
        </div>
      )}

      {/* Base daily routine (collapsible) */}
      <div className="rounded-2xl bg-white border border-rose-100">
        <button
          type="button"
          onClick={() => setShowBase((v) => !v)}
          data-testid="skincare-toggle-base"
          className="w-full flex items-center justify-between p-3"
        >
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold text-slate-800">🌸 Baspaket varje dag</div>
            <span className="text-[11px] text-slate-400">({plan.base_daily.length} steg)</span>
          </div>
          {showBase ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-500" />}
        </button>
        {showBase && (
          <div className="p-3 pt-0 space-y-1.5">
            {plan.base_daily.map((s) => {
              const done = doneStepIds.has(s.id);
              return (
                <div
                  key={s.id}
                  className={`flex items-start gap-2 p-2 rounded-lg ${done ? 'bg-emerald-50' : 'hover:bg-rose-50/40'}`}
                  data-testid={`skincare-base-${s.id}`}
                >
                  <button
                    type="button"
                    onClick={() => toggle(s.id, done)}
                    className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      done ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300 bg-white'
                    }`}
                    aria-label={done ? 'Ångra' : 'Klart'}
                  >
                    {done && <Check className="w-2.5 h-2.5" strokeWidth={3} />}
                  </button>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-900">{s.step}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{s.desc}</div>
                  </div>
                  <WhenBadge when={s.when} />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Weekly overview */}
      <div className="rounded-2xl bg-white border border-rose-100 p-4">
        <div className="text-sm font-semibold text-slate-800 mb-3">📅 Veckans rutiner</div>
        <div className="space-y-2">
          {plan.weekday_keys.map((k) => {
            const r = plan.weekly[k] || {};
            const isToday = k === today.day_key;
            const editing = editingDay === k;
            return (
              <div
                key={k}
                className={`rounded-xl p-3 border transition ${
                  isToday
                    ? 'bg-gradient-to-r from-rose-50 to-fuchsia-50 border-rose-300 ring-1 ring-rose-200'
                    : 'bg-white border-slate-200'
                }`}
                data-testid={`skincare-day-${k}`}
              >
                {editing ? (
                  <DayEditor
                    dayKey={k}
                    initial={r}
                    onCancel={() => setEditingDay(null)}
                    onSave={(u) => savePlan(k, u)}
                  />
                ) : (
                  <div className="flex items-start gap-2">
                    <div className="text-2xl leading-none flex-shrink-0 w-8 text-center">{r.emoji || plan.weekday_emojis[k]}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">{plan.weekday_labels[k]}</div>
                        {isToday && <span className="text-[10px] bg-rose-500 text-white rounded-full px-1.5 py-0.5 font-bold">IDAG</span>}
                      </div>
                      <div className="font-semibold text-slate-900 text-sm mt-0.5">{r.ritual || <span className="italic text-slate-400">Ingen rutin</span>}</div>
                      {r.desc && <div className="text-[11px] text-slate-600 mt-0.5 line-clamp-2">{r.desc}</div>}
                      <div className="flex flex-wrap gap-1 mt-1">
                        {r.when && <WhenBadge when={r.when} />}
                        {r.time_min ? (
                          <span className="text-[10px] rounded-full px-1.5 py-0.5 border bg-slate-50 border-slate-200 text-slate-700 inline-flex items-center gap-0.5">
                            <Clock className="w-2.5 h-2.5" /> {r.time_min} min
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setEditingDay(k)}
                      className="text-slate-300 hover:text-rose-600 flex-shrink-0"
                      data-testid={`skincare-edit-${k}`}
                      aria-label={`Redigera ${plan.weekday_labels[k]}`}
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Streak strip */}
      <div className="rounded-2xl bg-gradient-to-r from-amber-50 to-rose-50 border border-amber-200 p-3">
        <div className="text-xs font-semibold text-amber-900 mb-2 flex items-center gap-1">
          <Flame className="w-3 h-3" /> Streak — {streak.streak} dagar i rad
        </div>
        <div className="flex gap-1">
          {streak.days.map((d) => {
            const c = streak.counts[d] || 0;
            const dt = new Date(d + 'T00:00:00');
            const wd = ['sö', 'må', 'ti', 'on', 'to', 'fr', 'lö'][dt.getDay()];
            return (
              <div key={d} className="flex-1 flex flex-col items-center gap-0.5">
                <div className={`w-full aspect-square rounded ${c > 0 ? 'bg-gradient-to-br from-rose-400 to-fuchsia-500' : 'bg-white border border-slate-200'}`} title={d} />
                <div className="text-[9px] text-slate-500">{wd}</div>
              </div>
            );
          })}
        </div>
      </div>

      <p className="text-[11px] text-slate-400 italic text-center pt-2">
        💜 Klicka Edit-ikonen på valfri dag för att skräddarsy rutinen efter din hy och humör.
      </p>

      {/* Skin mood correlation insights */}
      {insights.length > 0 && (
        <div className="rounded-2xl bg-gradient-to-br from-violet-50 to-rose-50 border border-violet-200 p-4" data-testid="skincare-insights">
          <div className="text-sm font-semibold text-violet-900 flex items-center gap-1.5 mb-2">
            <TrendingUp className="w-3.5 h-3.5" /> Din huds mönster
            <span className="text-[10px] font-normal text-violet-600">(senaste 21 dagarna)</span>
          </div>
          <div className="space-y-1.5">
            {insights.map((i, idx) => (
              <div
                key={idx}
                className={`text-xs px-3 py-2 rounded-xl border ${
                  i.kind === 'warn'
                    ? 'bg-amber-50 border-amber-200 text-amber-900'
                    : 'bg-emerald-50 border-emerald-200 text-emerald-900'
                }`}
                data-testid={`insight-${idx}`}
              >
                {i.kind === 'warn' ? '⚠️ ' : '✨ '}{i.text}
              </div>
            ))}
          </div>
          <p className="text-[10px] text-violet-600/60 italic mt-2">
            Ju fler humör-loggar du gör, desto tydligare blir mönstren.
          </p>
        </div>
      )}

      {/* Product Inventory */}
      <ProductInventory />
    </div>
  );
}

function DayEditor({ dayKey, initial, onCancel, onSave }) {
  const [ritual, setRitual] = useState(initial.ritual || '');
  const [emoji, setEmoji] = useState(initial.emoji || '🌸');
  const [when, setWhen] = useState(initial.when || 'kväll');
  const [desc, setDesc] = useState(initial.desc || '');
  const [time_min, setTimeMin] = useState(initial.time_min || 10);

  const save = () => {
    if (!ritual.trim()) { toast.error('Ge rutinen ett namn'); return; }
    onSave({
      ritual: ritual.trim(),
      emoji: emoji || '🌸',
      when: when || 'kväll',
      desc: desc.trim(),
      time_min: parseInt(time_min, 10) || 10,
      tags: initial.tags || [],
      enabled: true,
    });
  };

  return (
    <div className="space-y-2" data-testid={`skincare-editor-${dayKey}`}>
      <div className="flex gap-2">
        <input
          type="text"
          value={emoji}
          onChange={(e) => setEmoji(e.target.value)}
          maxLength={4}
          className="w-14 text-2xl text-center rounded-lg border border-slate-200 py-1 focus:outline-none focus:ring-2 focus:ring-rose-200"
        />
        <input
          type="text"
          value={ritual}
          onChange={(e) => setRitual(e.target.value)}
          placeholder="Rutin-namn"
          data-testid={`skincare-editor-ritual-${dayKey}`}
          className="flex-1 text-sm rounded-lg border border-slate-200 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-rose-200"
        />
      </div>
      <textarea
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        placeholder="Beskrivning / instruktion"
        rows={2}
        maxLength={600}
        className="w-full text-xs rounded-lg border border-slate-200 p-2 focus:outline-none focus:ring-2 focus:ring-rose-200"
      />
      <div className="flex gap-2">
        <select value={when} onChange={(e) => setWhen(e.target.value)} className="text-xs rounded-lg border border-slate-200 px-2 py-1">
          <option value="morgon">🌅 morgon</option>
          <option value="kväll">🌙 kväll</option>
          <option value="morgon+kväll">☀️ morgon + kväll</option>
          <option value="duschtid">🚿 duschtid</option>
        </select>
        <input
          type="number"
          value={time_min}
          onChange={(e) => setTimeMin(e.target.value)}
          min="1"
          max="120"
          className="w-16 text-xs rounded-lg border border-slate-200 px-2 py-1 text-center"
        />
        <span className="text-xs text-slate-500 self-center">min</span>
        <div className="flex-1" />
        <button
          type="button"
          onClick={save}
          data-testid={`skincare-editor-save-${dayKey}`}
          className="text-xs px-3 py-1 rounded-full bg-rose-500 hover:bg-rose-600 text-white font-semibold inline-flex items-center gap-1"
        >
          <Save className="w-3 h-3" /> Spara
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs px-2 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center gap-1"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
